import { aiCoachGoalsList } from 'components/AICoach/Goals/Goals';
import { AICoachGoalItem } from 'api/LoanOfferApi';
import { AICoachGoalPriority, AICoachGoalType } from 'enums/AICoach';
import { DebtSummary, LoanType, Recommendation, Tradeline } from 'handlers/applicationData';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';

export interface TrajectoryMetric {
  label: string;
  value: string;
}

const DEFAULT_MONTHS = 12;

const formatMonths = (months: number): string => {
  const rounded = Math.max(1, Math.round(months));
  return `${rounded} ${rounded === 1 ? 'month' : 'months'}`;
};

const getTradelines = (debtSummary: DebtSummary | undefined, loanType?: LoanType): Tradeline[] => {
  if (!debtSummary) return [];

  if (loanType) {
    return debtSummary[loanType]?.tradelines ?? [];
  }

  return Object.values(debtSummary).flatMap((debt) => debt?.tradelines ?? []);
};

const sumBalances = (tradelines: Tradeline[]): number =>
  tradelines.reduce((total, tradeline) => total + (tradeline.balanceToConsolidate ?? 0), 0);

export const getPersonalDebtAmount = (debtSummary?: DebtSummary): number => {
  const consolidatableAmount = sumBalances(
    getTradelines(debtSummary).filter((tradeline) => tradeline.recommendation === Recommendation.PayOff),
  );

  if (consolidatableAmount > 0) return consolidatableAmount;

  const creditCards =
    debtSummary?.[LoanType.CreditCard]?.totalConsolidatableBalance ??
    debtSummary?.[LoanType.CreditCard]?.totalBalance ??
    0;
  const personalLoans =
    debtSummary?.[LoanType.PersonalLoan]?.totalConsolidatableBalance ??
    debtSummary?.[LoanType.PersonalLoan]?.totalBalance ??
    0;

  return creditCards + personalLoans;
};

const getMaxRemainingMonths = (tradelines: Tradeline[]): number | undefined => {
  const months = tradelines
    .map((tradeline) => tradeline.remainingMonths ?? tradeline.term)
    .filter((value): value is number => typeof value === 'number' && value > 0);

  if (months.length === 0) return undefined;
  return Math.max(...months);
};

export const getConsolidatableMaxRemainingMonths = (debtSummary?: DebtSummary): number | undefined => {
  const consolidatableTradelines = getTradelines(debtSummary).filter(
    (tradeline) => tradeline.recommendation === Recommendation.PayOff,
  );
  const tradelinesForPayoffTime =
    consolidatableTradelines.length > 0 ? consolidatableTradelines : getTradelines(debtSummary, LoanType.CreditCard);

  return getMaxRemainingMonths(tradelinesForPayoffTime);
};

const getStudentLoanTargetAmount = (debtSummary?: DebtSummary): number => {
  const tradelines = getTradelines(debtSummary, LoanType.StudentLoan);
  return debtSummary?.[LoanType.StudentLoan]?.totalBalance || sumBalances(tradelines);
};

export const getGoalTargetAmount = (goal?: AICoachGoalItem, debtSummary?: DebtSummary): number | undefined => {
  if (!goal) return undefined;

  if (goal.type === AICoachGoalType.PayDownStudentLoanDebt) {
    const amount = getStudentLoanTargetAmount(debtSummary);
    return amount > 0 ? amount : undefined;
  }

  return goal.targetValue;
};

export const getTimeToTargetFromGoal = (goal?: AICoachGoalItem, debtSummary?: DebtSummary): number => {
  const { monthlySavingsAmount } = goal ?? {};
  const targetAmount = getGoalTargetAmount(goal, debtSummary);

  if (targetAmount && monthlySavingsAmount && monthlySavingsAmount > 0) {
    return Math.ceil(targetAmount / monthlySavingsAmount);
  }
  return DEFAULT_MONTHS;
};

const getSavingsMetrics = (goal: AICoachGoalItem): TrajectoryMetric[] => [
  {
    label: 'Target amount',
    value: formatMonetaryAmount(goal.targetValue),
  },
  {
    label: 'Time to target',
    value: formatMonths(getTimeToTargetFromGoal(goal)),
  },
];

const getStudentLoanMetrics = (
  goal: AICoachGoalItem,
  debtSummary: DebtSummary | undefined,
): TrajectoryMetric[] | null => {
  const targetAmount = getGoalTargetAmount(goal, debtSummary);

  if (!targetAmount) return null;

  return [
    {
      label: 'Target amount',
      value: formatMonetaryAmount(targetAmount),
    },
    {
      label: 'Time to target',
      value: formatMonths(getTimeToTargetFromGoal(goal, debtSummary)),
    },
  ];
};

const getPayDebtMetrics = (debtSummary: DebtSummary | undefined): TrajectoryMetric[] | null => {
  const consolidatableTradelines = getTradelines(debtSummary).filter(
    (tradeline) => tradeline.recommendation === Recommendation.PayOff,
  );
  const totalDebt = sumBalances(consolidatableTradelines);
  const months = getConsolidatableMaxRemainingMonths(debtSummary);

  if (!totalDebt || months === undefined || months <= 0) return null;

  return [
    {
      label: 'Total debt',
      value: formatMonetaryAmount(totalDebt),
    },
    {
      label: 'Time to payoff',
      value: formatMonths(months),
    },
  ];
};

export const getSelectedAICoachGoal = (aiCoachGoals?: AICoachGoalItem[]): AICoachGoalItem | undefined => {
  if (!aiCoachGoals || aiCoachGoals.length === 0) return undefined;
  return aiCoachGoals.find((goal) => goal.priority === AICoachGoalPriority.Primary) ?? aiCoachGoals[0];
};

export const getYourGoalDisplayName = (goal: AICoachGoalItem): string => {
  if (goal.type === AICoachGoalType.SaveForSpecificExpense && goal.description) {
    return `Save for ${goal.description}`;
  }
  if (goal.type === AICoachGoalType.Other && goal.description) {
    return goal.description;
  }
  return aiCoachGoalsList.find((item) => item.value === goal.type)?.title ?? 'Your goal';
};

export const getYourGoalTrajectoryMetrics = (
  goal: AICoachGoalItem,
  debtSummary?: DebtSummary,
): TrajectoryMetric[] | null => {
  if (goal.type === AICoachGoalType.ImproveCreditScore) return null;

  if (goal.type === AICoachGoalType.PayDownCreditCardDebt) {
    return getPayDebtMetrics(debtSummary);
  }

  if (goal.type === AICoachGoalType.PayDownStudentLoanDebt) {
    return getStudentLoanMetrics(goal, debtSummary);
  }

  return getSavingsMetrics(goal);
};
